import React, { useState, useEffect } from 'react';
import { 
  X, 
  Building2,
  Save,
  Loader2
} from 'lucide-react';
import styles from './DepartmentDrawer.module.css';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const DepartmentDrawer = ({ isOpen, onClose, onSave, department = null }) => {
  const [formData, setFormData] = useState({
    departement_name: '',
  });

  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const fetchDepartmentDetail = async () => {
      if (department && isOpen) {
        setIsFetching(true);
        try {
          // Pre-populate with existing data from props while fetching
          setFormData({
            departement_name: department.departement_name || '',
          });

          const response = await api.get(`/departments/${department.id}`);
          if (response.data.success) {
            const data = response.data.data;
            setFormData({
              departement_name: data.departement_name || '',
            });
          }
        } catch (error) {
          console.error('Error fetching department details:', error);
          toast.error('Failed to fetch latest department details');
        } finally {
          setIsFetching(false);
        }
      } else {
        setFormData({
          departement_name: '',
        });
        setIsFetching(false);
      }
    };

    fetchDepartmentDetail();
  }, [isOpen, department]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let response;
      if (department) {
        response = await api.put(`/departments/${department.id}`, formData);
      } else {
        response = await api.post('/departments', formData);
      }

      if (response.data.success) {
        toast.success(department ? 'Department updated successfully' : 'Department added successfully');
        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Error saving department:', error);
      const message = error.response?.data?.message || 'Failed to save department';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{department ? 'Edit Department' : 'Add Department'}</h2>
            <p>Fill in the department details</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            {isFetching ? (
              <div className={styles['loading-container']}>
                <Loader2 size={40} className={styles['spinner']} />
                <span>Fetching department details...</span>
              </div>
            ) : (
              <div className={styles['form-group']}>
                <label><Building2 size={16} /> Department Name <span className={styles['required-star']}>*</span></label>
                <input 
                  type="text" 
                  name="departement_name"
                  value={formData.departement_name}
                  onChange={handleChange}
                  placeholder="Enter department name"
                  required
                />
              </div>
            )}
          </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading}>
              {loading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
              <span>{department ? 'Update Department' : 'Save Department'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DepartmentDrawer;
